import React, { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import api, { formatApiError, formatINR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { StatusBadge } from "./AdminDashboard";
import { Check, X, ChevronDown, ChevronUp, RotateCcw } from "lucide-react";

export default function Approvals() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [salespeople, setSalespeople] = useState([]);
  const [globalShares, setGlobalShares] = useState({ referral_share: 0.2, closure_share: 0.8 });
  const [expanded, setExpanded] = useState({}); // id -> bool
  const [drafts, setDrafts] = useState({}); // id -> { ref, close, marginEnabled, marginValue, splitEnabled, refPct, closePct }
  const [rejectFor, setRejectFor] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState({}); // id -> bool

  const load = (status) => api.get(`/invoices?status=${status}`).then((r) => setItems(r.data));
  useEffect(() => { load(tab); }, [tab]);
  useEffect(() => {
    api.get("/salespeople").then((r) => setSalespeople(r.data));
    api.get("/settings").then((r) => setGlobalShares(r.data));
  }, []);

  const draftFor = (inv) => {
    return (
      drafts[inv.id] || {
        ref: inv.customer_ref_by_id,
        close: inv.deal_closed_by_id,
        marginEnabled: inv.margin_override !== null && inv.margin_override !== undefined,
        marginValue: inv.margin_override != null ? inv.margin_override : inv.computed_margin,
        splitEnabled: inv.referral_share_override != null,
        refPct: inv.referral_share_override != null
          ? Math.round(inv.referral_share_override * 100)
          : Math.round((globalShares.referral_share || 0.2) * 100),
        closePct: inv.closure_share_override != null
          ? Math.round(inv.closure_share_override * 100)
          : Math.round((globalShares.closure_share || 0.8) * 100),
      }
    );
  };

  const setDraft = (inv, patch) => setDrafts((d) => ({ ...d, [inv.id]: { ...draftFor(inv), ...patch } }));

  const persistDraft = async (inv) => {
    const d = draftFor(inv);
    const payload = {};
    if (d.ref !== inv.customer_ref_by_id) payload.customer_ref_by_id = d.ref;
    if (d.close !== inv.deal_closed_by_id) payload.deal_closed_by_id = d.close;
    if (d.marginEnabled) {
      payload.margin_override = parseFloat(d.marginValue);
    } else if (inv.margin_override != null) {
      payload.clear_margin_override = true;
    }
    if (d.splitEnabled) {
      payload.referral_share_override = d.refPct / 100;
      payload.closure_share_override = d.closePct / 100;
    } else if (inv.referral_share_override != null) {
      payload.clear_split_override = true;
    }
    if (Object.keys(payload).length === 0) return inv; // nothing to save
    const res = await api.put(`/invoices/${inv.id}`, payload);
    return res.data;
  };

  const saveEdits = async (inv) => {
    setBusy((b) => ({ ...b, [inv.id]: true }));
    try {
      await persistDraft(inv);
      toast.success("Changes saved");
      setDrafts((d) => { const n = { ...d }; delete n[inv.id]; return n; });
      await load(tab);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy((b) => ({ ...b, [inv.id]: false }));
    }
  };

  const approveWithDraft = async (inv) => {
    setBusy((b) => ({ ...b, [inv.id]: true }));
    try {
      await persistDraft(inv);
      await api.post(`/invoices/${inv.id}/approval`, { action: "approve" });
      toast.success(`Approved #${inv.invoice_no}`);
      setDrafts((d) => { const n = { ...d }; delete n[inv.id]; return n; });
      load(tab);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy((b) => ({ ...b, [inv.id]: false }));
    }
  };

  const submitReject = async () => {
    if (!reason.trim()) { toast.error("Reason is required"); return; }
    try {
      await api.post(`/invoices/${rejectFor.id}/approval`, { action: "reject", rejection_reason: reason });
      toast.success(`Rejected ${rejectFor.invoice_no}`);
      setRejectFor(null);
      setReason("");
      load(tab);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const onRefPctChange = (inv, v) => {
    const n = Math.max(0, Math.min(100, parseFloat(v) || 0));
    setDraft(inv, { refPct: n, closePct: 100 - n });
  };
  const onClosePctChange = (inv, v) => {
    const n = Math.max(0, Math.min(100, parseFloat(v) || 0));
    setDraft(inv, { closePct: n, refPct: 100 - n });
  };

  return (
    <Shell title="Approvals" subtitle="Edit Ref By / Closed By / split ratio (and optionally margin) before approving.">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
        </TabsList>
        <TabsContent value={tab}>
          <Card className="border-zinc-200 shadow-none rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-zinc-50">
                  <TableHead className="font-bold text-zinc-950 w-10"></TableHead>
                  <TableHead className="font-bold text-zinc-950">Invoice</TableHead>
                  <TableHead className="font-bold text-zinc-950">Date</TableHead>
                  <TableHead className="font-bold text-zinc-950">Customer · Project</TableHead>
                  <TableHead className="font-bold text-zinc-950">Ref by</TableHead>
                  <TableHead className="font-bold text-zinc-950">Closed by</TableHead>
                  <TableHead className="font-bold text-zinc-950 text-right">Value</TableHead>
                  <TableHead className="font-bold text-zinc-950 text-right">Margin</TableHead>
                  <TableHead className="font-bold text-zinc-950 text-right">Status / Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="text-center py-10 text-zinc-500">No invoices.</TableCell></TableRow>
                )}
                {items.map((inv) => {
                  const d = draftFor(inv);
                  const isOpen = !!expanded[inv.id];
                  const dirty = drafts[inv.id] !== undefined;
                  return (
                    <React.Fragment key={inv.id}>
                      <TableRow data-testid={`approval-row-${inv.id}`} className={dirty ? "bg-amber-50/40" : ""}>
                        <TableCell>
                          {tab === "pending" && (
                            <button onClick={() => toggleExpand(inv.id)} data-testid={`expand-${inv.id}`} className="p-1 rounded hover:bg-zinc-100">
                              {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </button>
                          )}
                        </TableCell>
                        <TableCell className="font-semibold font-mono text-xs">{inv.invoice_no}</TableCell>
                        <TableCell>{inv.invoice_date?.slice(0, 10)}</TableCell>
                        <TableCell>
                          <div className="font-medium">{inv.customer_name}</div>
                          {inv.project && <div className="text-xs text-zinc-500">{inv.project}</div>}
                        </TableCell>
                        <TableCell>{inv.customer_ref_by_name}</TableCell>
                        <TableCell>{inv.deal_closed_by_name}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatINR(inv.invoice_value)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatINR(inv.margin)}</TableCell>
                        <TableCell className="text-right">
                          {tab === "pending" ? (
                            <div className="flex gap-2 justify-end">
                              <Button size="sm" disabled={busy[inv.id]} onClick={() => approveWithDraft(inv)} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid={`approve-${inv.id}`}>
                                <Check size={14} className="mr-1" /> Approve
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => { setRejectFor(inv); setReason(""); }} data-testid={`reject-${inv.id}`}>
                                <X size={14} className="mr-1" /> Reject
                              </Button>
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <StatusBadge status={inv.status} />
                              {inv.rejection_reason && (
                                <div className="text-xs text-rose-700 italic" data-testid={`reason-${inv.id}`}>{inv.rejection_reason}</div>
                              )}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>

                      {tab === "pending" && isOpen && (
                        <TableRow data-testid={`edit-panel-${inv.id}`}>
                          <TableCell colSpan={9} className="bg-zinc-50/60 border-t border-zinc-200">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-2">
                              <div>
                                <Label className="font-semibold text-xs uppercase tracking-wider text-zinc-500">Customer Ref By</Label>
                                <Select value={d.ref} onValueChange={(v) => setDraft(inv, { ref: v })}>
                                  <SelectTrigger data-testid={`edit-ref-${inv.id}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {salespeople.map((sp) => <SelectItem key={sp.id} value={sp.id}>{sp.name}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label className="font-semibold text-xs uppercase tracking-wider text-zinc-500">Deal Closed By</Label>
                                <Select value={d.close} onValueChange={(v) => setDraft(inv, { close: v })}>
                                  <SelectTrigger data-testid={`edit-close-${inv.id}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {salespeople.map((sp) => <SelectItem key={sp.id} value={sp.id}>{sp.name}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>

                              <div className="md:col-span-2 p-3 border border-zinc-200 rounded-md bg-white">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Split ratio</div>
                                  <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={d.splitEnabled}
                                      onChange={(e) => setDraft(inv, { splitEnabled: e.target.checked })}
                                      data-testid={`split-toggle-${inv.id}`}
                                    />
                                    Override for this invoice
                                  </label>
                                </div>
                                {d.splitEnabled ? (
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <Label className="text-xs font-semibold">Referral %</Label>
                                      <Input type="number" min="0" max="100" value={d.refPct} onChange={(e) => onRefPctChange(inv, e.target.value)} data-testid={`ref-pct-${inv.id}`} />
                                    </div>
                                    <div>
                                      <Label className="text-xs font-semibold">Closure %</Label>
                                      <Input type="number" min="0" max="100" value={d.closePct} onChange={(e) => onClosePctChange(inv, e.target.value)} data-testid={`close-pct-${inv.id}`} />
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-sm text-zinc-500">
                                    Using global default: <span className="font-semibold text-zinc-700">{Math.round(globalShares.referral_share * 100)}% referral · {Math.round(globalShares.closure_share * 100)}% closure</span>
                                  </div>
                                )}
                              </div>

                              <div className="md:col-span-2 p-3 border border-zinc-200 rounded-md bg-white">
                                <div className="flex items-center justify-between mb-2">
                                  <div>
                                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Margin</div>
                                    <div className="text-xs text-zinc-500">Computed: {formatINR(inv.computed_margin)}</div>
                                  </div>
                                  <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={d.marginEnabled}
                                      onChange={(e) => setDraft(inv, { marginEnabled: e.target.checked })}
                                      data-testid={`margin-toggle-${inv.id}`}
                                    />
                                    Override margin
                                  </label>
                                </div>
                                {d.marginEnabled && (
                                  <Input
                                    type="number"
                                    value={d.marginValue}
                                    onChange={(e) => setDraft(inv, { marginValue: e.target.value })}
                                    data-testid={`margin-value-${inv.id}`}
                                  />
                                )}
                              </div>

                              <div className="md:col-span-2 flex justify-end gap-2">
                                {dirty && (
                                  <Button variant="ghost" size="sm" onClick={() => setDrafts((dr) => { const n = { ...dr }; delete n[inv.id]; return n; })} data-testid={`reset-${inv.id}`}>
                                    <RotateCcw size={14} className="mr-1" /> Reset
                                  </Button>
                                )}
                                <Button size="sm" variant="outline" onClick={() => saveEdits(inv)} disabled={!dirty || busy[inv.id]} data-testid={`save-edits-${inv.id}`}>
                                  Save changes
                                </Button>
                                <Button size="sm" onClick={() => approveWithDraft(inv)} disabled={busy[inv.id]} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid={`approve-with-edits-${inv.id}`}>
                                  <Check size={14} className="mr-1" /> Save & Approve
                                </Button>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent className="sm:max-w-md" data-testid="reject-dialog">
          <DialogHeader><DialogTitle>Reject Invoice #{rejectFor?.invoice_no}</DialogTitle></DialogHeader>
          <Textarea
            placeholder="Reason for rejection (required)…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="reject-reason-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectFor(null)}>Cancel</Button>
            <Button onClick={submitReject} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="reject-confirm-button">
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
